import React, { memo, useMemo, useState, useCallback, useEffect, useContext } from 'react';
import { H3 } from 'tea-component';
import { SortBy } from "tea-component/table/addons";
import TriggerViewCard from '@gp/components/TriggerViewCard';
// import SvgIcon from '@gp/components/Iconfont';
import MergeableTable from './components/MergeableTable';
import TableForPDFDownload from './components/MergeableTable/TableForPDFDownload';
import LineEchartCon from './components/LineEchartCon';
import BarEchartCon from './components/BarEchartCon';
import { context as Context } from '@gp/components/Context';
import { isEqual } from 'lodash-es';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import { useRequest, useMemoizedFn } from 'ahooks';
import {
  downloadHeadcountsOfPeople,
  queryHeadcountsList,
  queryAllHeadcountsOfPeople
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { getStudioModuleName } from '../../../../../utils';
import { FILE_TYPES1, FILE_TYPES_MAP } from '@gp/constants/const';
import { IFilter } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/People/Details/components/Filter';
import useHeadcountsList from '../../hook/useHeadcountsList';
import useDownloadTablePDF from '../../hook/useDownloadTablePDF';
// import { formatMonth } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/People/utils/common';
import './index.scss';

import SvgIcon from '@/pages/home/components/SvgIcon';
import CardBtn from '@/pages/globalpedia/components/CardBtn';

interface HeadcountsPropsType {
  filter: IFilter;
}

// Headcounts模块名称
const MODULE_NAME = 'Headcounts';

// Headcounts模块 PDF下载 需要用到的className
const PDF_DOWNLOAD_NODE_CLASSNAME =
  'dbg-studio-overall-details-people-headcounts-pdf-card';

const DEFAULT_PROPS: HeadcountsPropsType = {
  filter: {
    date_type: 'Month',
    start_date: '',
    end_date: '',
  },
};

function Headcounts(props: HeadcountsPropsType) {
  const { filter: monthFilter } = props;

  const [filter, setFilter] = useState<FilterType>({
    // 类型: 1:function 2:projects
    type: '1',
    start_date: '',
    end_date: '',
  });
  // 之前筛选器请求过的数据，用于function/projects反复切换时减少接口请求（当顶部月份筛选器日期变化时，数据初始化刷新）
  const [lastFilterHistoryDataList, setLastFilterHistoryDataListDataList] =
    useState<orgHeadcountsLastFilterHistoryDataTypes>([]);
  const [dataList, setDataList] = useState<orgHeadcountsDataTypes>(null);
  const [sorts, setSorts] = useState<SortBy>({
    by: '',
    order: undefined,
  });

  const { studioId, studioName }: StudioOverallDetailContextTypes = useContext(Context) || {};

  const {
    data: responseRes,
    loading,
    run,
  } = useRequest(queryHeadcountsList, {manual: true});

  // 处理接口数据
  const {
    orgData,
  } = useHeadcountsList({
    loading,
    responseRes,
    module: 'headcounts',
  });

  const {
    isAllDatasResLoading,
    allDatas,
    updateAllDatas,
  } = useDownloadTablePDF(
    queryAllHeadcountsOfPeople,
    'headcounts',
  );

  const { isDownloading, setIsDownloading, handleDownload } = useDownload({
    fileName: getStudioModuleName(MODULE_NAME, studioName),
    csvDownloadLogKey: 'STUDIO_OVERALL_DETAIL_PEOPLE_HEADCOUNTS_CSV_SUBMIT',
    pdfDownloadLogKey: 'STUDIO_OVERALL_DETAIL_PEOPLE_HEADCOUNTS_PDF_SUBMIT',
    node: document.querySelector(`.${PDF_DOWNLOAD_NODE_CLASSNAME}`),
    downloadService: createDownloadService(downloadHeadcountsOfPeople),
    nodeOptions: {
      ownerStyle: 'visibility: visible;',
    },
  });

  // 月份筛选器发生变化
  useEffect(() => {
    if (!monthFilter?.start_date || !monthFilter?.end_date) return;
    setFilter({
      ...filter,
      start_date: monthFilter?.start_date,
      end_date: monthFilter?.end_date,
    });
    setLastFilterHistoryDataListDataList([]);
  }, [monthFilter?.start_date, monthFilter?.end_date]);

  // 总体筛选器条件发生变化
  useEffect(() => {
    if (
      !filter?.start_date ||
      !filter?.end_date ||
      !['1', '2'].includes(filter?.type)
    )
      return;
    // 对比是否拥有历史数据
    const hadDataList =
      lastFilterHistoryDataList?.find((t) => {
        const had =
          t?.filterSign?.type === filter?.type &&
          t?.filterSign?.start_date === filter?.start_date &&
          t?.filterSign?.end_date === filter?.end_date;
        return had;
      })?.dataList || undefined;
    // 当之前有请求过筛选器条件数据时，用之前的
    if (hadDataList) {
      setDataList(hadDataList);
      return;
    }
    // 请求接口
    run({
      studio_id: studioId,
      start_date: filter?.start_date, // 2023-01-01
      end_date: filter?.end_date, // 2023-12-01
      type: Number(filter?.type) as (1 | 2), // 1: function 2: projects
      // order_metric: formatMonth(sorts?.by),
      order_metric: sorts?.by,
      order: sorts?.order,
    });
  }, [filter?.start_date, filter?.end_date, filter?.type]);

  // 接口返回数据
  useEffect(() => {
    if (loading) return;
    // 接口请求失败
    if (!orgData?.isSuccessReq) {
      setDataList(null);
      return;
    }
    setLastFilterHistoryDataListDataList([
      ...lastFilterHistoryDataList,
      {
        filterSign: filter,
        dataList: orgData,
      },
    ]);
    setDataList(orgData);
  }, [orgData, loading]);

  const handleChange = useCallback(
    (type: HeadcountsDataType) => {
      // 恢复默认排序
      setSorts({
        by: '',
        order: undefined,
      });
      const newFilter = {
        ...filter,
        type: type || '1',
      };
      setFilter(newFilter);
    },
    [filter],
  );

  const handleSortsChange = useMemoizedFn((sorts: SortBy) => {
    setSorts(sorts);
    run({
      studio_id: studioId,
      start_date: filter?.start_date, // 2023-01-01
      end_date: filter?.end_date, // 2023-12-01
      type: Number(filter?.type) as (1 | 2), // 1: function 2: projects
      // order_metric: formatMonth(sorts?.by),
      order_metric: sorts?.by,
      order: sorts?.order,
    });
  });

  const tabs = useMemo(() => {
    return [
      {
        id: 'chart-bar',
        label: <CardBtn onClick={() => {}} type="column-chart" />,
        component: <BarEchartCon />,
      },
      {
        id: 'chart-line',
        label: <CardBtn onClick={() => {}} type="discount-chart" />,
        component: <LineEchartCon />,
      },
      {
        id: 'table',
        label: <CardBtn onClick={() => {}} type="sheet" />,
        component: (
          <MergeableTable
            sorts={sorts}
            onSortsChange={handleSortsChange}
          />
        ),
      },
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES1}
            isLoading={isDownloading}
            onConfirm={async (payload) => {
              if (isDownloading) return;
              const params = {
                ...payload,
                file_type: Number.parseInt(payload?.fileType),
                people_headcounts: {
                  studio_id: studioId,
                  type: Number.parseInt(filter?.type),
                  start_date: filter?.start_date,
                  end_date: filter?.end_date,
                  // order_metric: formatMonth(sorts?.by),
                  order_metric: sorts?.by,
                  order: sorts?.order,
                },
              };
              if (payload?.fileType === FILE_TYPES_MAP.PDF) {
                setIsDownloading?.(true);
                await updateAllDatas?.(params);
                setIsDownloading?.(false);
              }
              handleDownload?.(params);
            }}
          />
        ),
        component: null,
      },
    ];
  }, [studioId, filter?.type, filter?.start_date, filter?.end_date, isDownloading, setIsDownloading, handleDownload, sorts]);

  const pdfTabs = useMemo(() => {
    return [
      {
        id: 'table',
        label: null,
        component: <TableForPDFDownload />,
      },
    ];
  }, []);

  return (
    <Context.Provider
      value={{
        loading,
        dataList,
        isAllDatasResLoading,
        allDatas,
        filter,
        sorts,
        onChange: handleChange,
      }}
    >
      <div className="tea-card dbg-studio-overall-details-people-headcounts-card-container">
        <TriggerViewCard
          className="dbg-studio-overall-details-people-headcounts-content"
          title={<H3>{MODULE_NAME}</H3>}
          loading={false}
          isNoData={false}
          tabs={tabs}
          defaultActiveId="chart-bar"
        />
        <TriggerViewCard
          className="dbg-studio-overall-details-people-headcounts-content"
          // 这个rootNodeClassName 是为了 下载PDF用的 不要删除
          rootNodeClassName={PDF_DOWNLOAD_NODE_CLASSNAME}
          title={<H3>{MODULE_NAME}</H3>}
          loading={false}
          isNoData={false}
          tabs={pdfTabs}
          defaultActiveId="table"
        />
      </div>
    </Context.Provider>
  );
}

Headcounts.defaultProps = DEFAULT_PROPS;

export default memo(Headcounts, (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
});
